import { onCleanup, onMount } from 'solid-js';
import './ThemedCursor.css';

const TEXT_SELECTOR = 'textarea, [contenteditable="true"], input:not([type]), input[type="email"], input[type="number"], input[type="password"], input[type="search"], input[type="tel"], input[type="text"], input[type="url"]';
const DISABLED_SELECTOR = ':disabled, [aria-disabled="true"]';
const RESIZE_SELECTOR = '.v2-window__handle';
const DRAG_SELECTOR = '.v2-window__hdr, .v2-window__borderless-drag, .v2-home__tab-item[draggable="true"], .v2-home__dash-item[draggable="true"], .v2-desktop-icon.is-dragging';
const HELP_SELECTOR = '[data-tip-trigger], .v2-tip-trigger, .v2-mtip-wrap, .v2-pc-price__main, .v2-pc-trend, .v2-gt-axis-peak, .v2-gt-axis-total, .v2-admin-badge, .v2-fissal-suggestion';
const INTERACTIVE_SELECTOR = 'a[href], button, select, summary, input[type="button"], input[type="checkbox"], input[type="radio"], input[type="range"], input[type="reset"], input[type="submit"], [role="button"], [role="menuitem"], [role="tab"], [data-ui-action], .btn, .v2-btn-sm, .v2-taskbar__start, .v2-taskbar__win, .v2-taskbar__tray-btn, .v2-taskbar__clock, .v2-taskbar__moon-pop button, .v2-launcher__btn, .v2-launcher__panel li, .v2-launcher__clear, .v2-home__tab, .v2-home__theme-opt, .v2-home__btn, .v2-context-menu__item, .v2-hover-card, .v2-pick, .v2-kiosk__hist-guild, .v2-tbh__row, .v2-asales__th-btn, .v2-desktop-icon';

const STATE_CLASSES = ['is-hover', 'is-press', 'is-release', 'is-drag', 'is-resize', 'is-help', 'is-disabled', 'is-text'];

function pointerIsMouse(event) {
  return !event.pointerType || event.pointerType === 'mouse';
}

function closestElement(target) {
  return target instanceof Element ? target : target?.parentElement || null;
}

function targetState(target) {
  const element = closestElement(target);
  if (!element) return 'idle';
  if (element.closest(DISABLED_SELECTOR)) return 'disabled';
  if (element.closest(TEXT_SELECTOR)) return 'text';
  if (element.closest(RESIZE_SELECTOR)) return 'resize';
  if (element.closest(DRAG_SELECTOR)) return 'drag';
  if (element.closest(HELP_SELECTOR)) return 'help';
  if (element.closest(INTERACTIVE_SELECTOR)) return 'hover';
  return 'idle';
}

export default function ThemedCursor() {
  let cursorNode;
  let lastState = 'idle';
  let appliedState = 'idle';
  let lastTarget = null;
  let isVisible = false;
  let pressed = false;
  let releaseTimer = 0;
  let moveRaf = 0;
  let pendingX = -64;
  let pendingY = -64;

  function applyState(nextState) {
    if (!cursorNode || nextState === appliedState) return;
    appliedState = nextState;
    cursorNode.classList.remove(...STATE_CLASSES);
    if (nextState !== 'idle') cursorNode.classList.add(`is-${nextState}`);
    cursorNode.dataset.state = nextState;
  }

  function flushMove() {
    moveRaf = 0;
    if (!cursorNode) return;
    cursorNode.style.setProperty('--cursor-x', `${pendingX}px`);
    cursorNode.style.setProperty('--cursor-y', `${pendingY}px`);
    if (!isVisible) {
      isVisible = true;
      cursorNode.classList.add('is-visible');
    }
  }

  function moveTo(event, immediate = false) {
    if (!cursorNode || !pointerIsMouse(event)) return;
    pendingX = event.clientX;
    pendingY = event.clientY;
    if (immediate) {
      if (moveRaf) {
        window.cancelAnimationFrame(moveRaf);
        moveRaf = 0;
      }
      flushMove();
    } else if (!moveRaf) {
      moveRaf = window.requestAnimationFrame(flushMove);
    }
  }

  function syncTarget(event, force = false) {
    if (!cursorNode || !pointerIsMouse(event)) return;
    if (!force && event.target === lastTarget) return;
    lastTarget = event.target;
    lastState = targetState(event.target);
    if (!pressed && !releaseTimer) applyState(lastState);
  }

  function clearReleaseTimer() {
    if (!releaseTimer) return;
    window.clearTimeout(releaseTimer);
    releaseTimer = 0;
  }

  function handlePointerMove(event) {
    moveTo(event, false);
    if (event.target !== lastTarget) syncTarget(event);
  }

  function handlePointerOver(event) {
    moveTo(event, false);
    syncTarget(event, true);
  }

  function handlePointerDown(event) {
    if (!pointerIsMouse(event) || event.button !== 0) return;
    moveTo(event, true);
    pressed = true;
    clearReleaseTimer();
    applyState('press');
  }

  function handlePointerUp(event) {
    if (!pointerIsMouse(event)) return;
    moveTo(event, true);
    pressed = false;
    const hovered = document.elementFromPoint(event.clientX, event.clientY);
    lastTarget = hovered || event.target;
    lastState = targetState(lastTarget);
    applyState('release');
    clearReleaseTimer();
    releaseTimer = window.setTimeout(() => {
      releaseTimer = 0;
      applyState(lastState);
    }, 180);
  }

  function hideCursor() {
    pressed = false;
    lastTarget = null;
    clearReleaseTimer();
    if (moveRaf) {
      window.cancelAnimationFrame(moveRaf);
      moveRaf = 0;
    }
    if (cursorNode && isVisible) {
      isVisible = false;
      cursorNode.classList.remove('is-visible');
    }
    applyState('idle');
  }

  onMount(() => {
    if (!window.matchMedia?.('(pointer: fine)').matches) return;
    if (cursorNode && cursorNode.parentElement !== document.body) {
      document.body.appendChild(cursorNode);
    }
    document.body.classList.add('v2-theme-cursor-active');
    document.addEventListener('pointermove', handlePointerMove, { capture: true, passive: true });
    document.addEventListener('pointerover', handlePointerOver, { capture: true, passive: true });
    document.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: true });
    document.addEventListener('pointerup', handlePointerUp, { capture: true, passive: true });
    document.addEventListener('pointercancel', hideCursor, { capture: true, passive: true });
    document.addEventListener('pointerleave', hideCursor, { capture: true, passive: true });
    window.addEventListener('blur', hideCursor);
  });

  onCleanup(() => {
    clearReleaseTimer();
    if (moveRaf) window.cancelAnimationFrame(moveRaf);
    document.body.classList.remove('v2-theme-cursor-active');
    document.removeEventListener('pointermove', handlePointerMove, true);
    document.removeEventListener('pointerover', handlePointerOver, true);
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('pointerup', handlePointerUp, true);
    document.removeEventListener('pointercancel', hideCursor, true);
    document.removeEventListener('pointerleave', hideCursor, true);
    window.removeEventListener('blur', hideCursor);
    cursorNode?.remove();
  });

  return (
    <div class="v2-theme-cursor" ref={cursorNode} aria-hidden="true" data-state="idle">
      <svg class="v2-theme-cursor__svg" viewBox="0 0 32 32" focusable="false">
        <path class="v2-theme-cursor__shape v2-theme-cursor__arrow" d="M5 3 5 27 11 21 15 30 20 28 16 20 25 20Z" />
        <path class="v2-theme-cursor__shape v2-theme-cursor__hand" d="M14.2 3.5c-1.1 0-2 .9-2 2v9.7l-1.8-1.8c-1-1-2.5-1-3.5 0-.9.9-.9 2.4 0 3.4l4.8 5.6c1.6 1.9 3.8 3 6.3 3h3c3.7 0 6.6-3 6.6-6.6v-6c0-1.1-.9-2-2-2-.8 0-1.4.4-1.8 1-.3-.9-1-1.5-2-1.5-.8 0-1.4.4-1.8 1-.3-.9-1-1.5-2-1.5-.7 0-1.3.3-1.7.9V5.5c0-1.1-.9-2-2.1-2Z" />
        <path class="v2-theme-cursor__shape v2-theme-cursor__resize" d="M16 4 21 9h-3v5h5v-3l5 5-5 5v-3h-5v5h3l-5 5-5-5h3v-5H9v3l-5-5 5-5v3h5V9h-3Z" />
        <path class="v2-theme-cursor__disabled" d="M7 25 25 7" />
      </svg>
    </div>
  );
}